import {
    AlertTriangle,
    ArrowRight,
    ChevronLeft,
    Clock3,
    LoaderCircle,
    RefreshCw,
    Trash2,
    WalletCards,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './WriteOffAdvanceScreen.module.css'

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const formatINR = (amount) => {
    const num = parseFloat(amount)
    if (!isFinite(num)) return '—'
    return num.toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })
}

const WriteOffAdvanceScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { advanceIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)
    const token = authCtx?.token

    const advance = location?.state?.advance || null

    const [preview, setPreview] = useState(null)
    const [confirmationToken, setConfirmationToken] = useState(null)
    const [expiresIn, setExpiresIn] = useState(null)
    const [writeOffReason, setWriteOffReason] = useState('')
    const [reasonError, setReasonError] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isExecuting, setIsExecuting] = useState(false)

    const fetchWriteOffPreview = useCallback(async () => {
        if (!token || !activeStore?.storeIdentifier || !advanceIdentifier) return

        try {
            setIsLoading(true)

            const response = await fetch(BASE_URL_API + 'writeOffStaffAdvance', {
                method: 'POST',
                body: JSON.stringify({
                    token,
                    storeIdentifier: activeStore.storeIdentifier,
                    advanceIdentifier,
                }),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                setPreview(respo.response)
                setConfirmationToken(respo.response.confirmationToken)
                setExpiresIn(respo.response.confirmationTokenExpiresIn ?? null)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            toast.error(respo?.message || 'Failed to load write-off preview.', {
                position: 'bottom-center',
            })
            navigate(-1)
        } catch {
            toast.error('Network error.', { position: 'bottom-center' })
        } finally {
            setIsLoading(false)
        }
    }, [token, activeStore?.storeIdentifier, advanceIdentifier]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchWriteOffPreview()
    }, [fetchWriteOffPreview])

    // Countdown ticker
    useEffect(() => {
        if (expiresIn === null) return
        if (expiresIn <= 0) return
        const interval = setInterval(() => {
            setExpiresIn((prev) => {
                if (prev === null || prev <= 1) {
                    clearInterval(interval)
                    return 0
                }
                return prev - 1
            })
        }, 1000)
        return () => clearInterval(interval)
    }, [expiresIn])

    const executeWriteOff = async () => {
        if (!writeOffReason.trim()) {
            setReasonError('Provide a reason for writing off this advance.')
            return
        }
        setReasonError(null)

        try {
            setIsExecuting(true)

            const response = await fetch(BASE_URL_API + 'writeOffStaffAdvance', {
                method: 'POST',
                body: JSON.stringify({
                    token,
                    storeIdentifier: activeStore.storeIdentifier,
                    advanceIdentifier,
                    confirmationToken,
                    writeOffReason: writeOffReason.trim(),
                    writtenOffBy: authCtx?.staffIdentifier || authCtx?.authIdentifier || '',
                }),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                toast.success(
                    `₹${formatINR(respo.response.amountWrittenOff)} written off for ${respo.response.staffName}.`,
                    { position: 'bottom-center' }
                )
                navigate(-1)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'ADV_005':
                    toast.info('Session expired. Refreshing confirmation…', {
                        position: 'bottom-center',
                    })
                    fetchWriteOffPreview()
                    break
                case 'ADV_004':
                    toast.error('Invalid confirmation. Please try again.', {
                        position: 'bottom-center',
                    })
                    navigate(-1)
                    break
                case 'VAL_001':
                    setReasonError(respo.message)
                    break
                case 'ADV_002':
                    toast.error(
                        `Advance is already "${respo.response?.currentStatus || 'updated'}".`,
                        { position: 'bottom-center' }
                    )
                    navigate(-1)
                    break
                default:
                    toast.error(respo?.message || 'Write-off failed.', {
                        position: 'bottom-center',
                    })
            }
        } catch {
            toast.error('Network error.', { position: 'bottom-center' })
        } finally {
            setIsExecuting(false)
        }
    }

    const isBusy = isLoading || isExecuting

    if (!advanceIdentifier) {
        return (
            <div className={styles.page}>
                <div className={styles.alertCard}>
                    <AlertTriangle size={16} />
                    <div>
                        <strong>No advance selected.</strong>
                        <p>Open this screen from an active advance card.</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            {/* ── Header ── */}
            <div className={styles.header}>
                <button
                    className={styles.backBtn}
                    type="button"
                    onClick={() => navigate(-1)}
                    disabled={isBusy}
                >
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}>
                        <Trash2 size={12} />
                        Destructive Action
                    </span>
                    <h1 className={styles.headerTitle}>Write Off Advance</h1>
                </div>
            </div>

            {/* ── Loading skeleton ── */}
            {isLoading && !preview ? (
                <div className={styles.loadingState}>
                    <LoaderCircle size={24} className={styles.spinIcon} />
                    <span>Loading write-off preview…</span>
                </div>
            ) : null}

            {/* ── Preview card ── */}
            {preview ? (
                <>
                    <div className={styles.dangerBanner}>
                        <AlertTriangle size={15} />
                        <div>
                            <strong>Permanent Action — Cannot Be Undone</strong>
                            <p>{preview.message}</p>
                        </div>
                    </div>

                    <div className={styles.previewCard}>
                        <div className={styles.previewHead}>
                            <WalletCards size={14} />
                            <span>Write-Off Summary</span>
                        </div>
                        <div className={styles.previewGrid}>
                            <div className={styles.previewCell}>
                                <span>Staff</span>
                                <strong>{preview.staffName || advance?.staffName || '—'}</strong>
                            </div>
                            <div className={styles.previewCell}>
                                <span>Disbursed</span>
                                <strong>₹{formatINR(preview.disbursedAmount)}</strong>
                            </div>
                            <div className={styles.previewCell}>
                                <span>Repaid</span>
                                <strong>₹{formatINR(preview.totalAlreadyRepaid)}</strong>
                            </div>
                            <div className={`${styles.previewCell} ${styles.previewCellDanger}`}>
                                <span>Write Off</span>
                                <strong>₹{formatINR(preview.amountToBeWrittenOff)}</strong>
                            </div>
                        </div>
                    </div>

                    {/* ── Token expiry ── */}
                    {expiresIn !== null ? (
                        <div className={expiresIn > 60 ? styles.expiryBadge : styles.expiryBadgeWarn}>
                            <Clock3 size={13} />
                            {expiresIn > 0 ? (
                                <span>Confirmation valid for ~{expiresIn}s</span>
                            ) : (
                                <span>
                                    Token expired —{' '}
                                    <button
                                        type="button"
                                        className={styles.refreshLink}
                                        onClick={fetchWriteOffPreview}
                                    >
                                        <RefreshCw size={12} /> Refresh
                                    </button>
                                </span>
                            )}
                        </div>
                    ) : null}

                    {/* ── Reason field ── */}
                    <div className={styles.formCard}>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel} htmlFor="writeOffReason">
                                Reason for Write-Off *
                            </label>
                            <textarea
                                id="writeOffReason"
                                className={`${styles.textArea} ${reasonError ? styles.inputError : ''}`}
                                rows={4}
                                value={writeOffReason}
                                placeholder="e.g. Staff resigned, Medical hardship, Goodwill gesture"
                                onChange={(e) => {
                                    setWriteOffReason(e.target.value)
                                    if (reasonError) setReasonError(null)
                                }}
                                disabled={isExecuting}
                            />
                            {reasonError ? (
                                <span className={styles.errorText}>{reasonError}</span>
                            ) : null}
                        </div>

                        <button
                            type="button"
                            className={styles.writeOffBtn}
                            onClick={executeWriteOff}
                            disabled={isBusy || expiresIn === 0}
                        >
                            {isExecuting ? (
                                <>
                                    <LoaderCircle size={15} className={styles.spinIcon} />
                                    Processing…
                                </>
                            ) : (
                                <>
                                    Write Off ₹{formatINR(preview.amountToBeWrittenOff)}
                                    <ArrowRight size={14} />
                                </>
                            )}
                        </button>
                    </div>
                </>
            ) : null}
        </div>
    )
}

export default WriteOffAdvanceScreen